import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  MengenEingabeComponent,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS } from '@softline/application';
import { BehaviorSubject, map, Subscription, switchMap } from 'rxjs';
import {
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE } from '../../../../kommissionierung.shared';
import { LlrBestellungStore } from '../../../../store/bestellung.store';
import { LlrKommissionierungStore } from '../../../../store/kommissionierung.store';
import {
  Kommissionierung,
  KommissionierungBewegung } from '../../../../types/kommissionierung';
import { Store } from '@softline/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RemoteConfigService } from '../../../../services/remote-config.service';

import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-offene-menge-erfassung',
  imports: [CommonModule, MengenEingabeComponent,
    RepeatDirective,
    TranslatePipe],
  templateUrl: './offene-menge-erfassung.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./offene-menge-erfassung.component.css'] })
export class OffeneMengeErfassungComponent implements OnInit, OnDestroy, BackNavigable {
  private store = inject(Store);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private remoteConfig = inject(RemoteConfigService);
  private backNavigationService = inject(BackNavigationService);

  private kommissionierung?: Kommissionierung;
  private kommissionierungSubscription?: Subscription;
  private kommissionierMengeLocal?: number;
  private gewichtValidOrNotNeeded$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  public kommissionierGewichtLocal?: number;
  public calculatedAnzahlPaletten = 0;
  public calculatedAnzahlRestmenge = 0;
  public vorhandenePalettenAuswaehlbar = false;
  public gewichtEditable = false;
  public paletteEinzelEingabe = false;

  readonly palettenId = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((queryParamMap) => queryParamMap.get('paletteId') ?? null)
    )
  );

  readonly palettenVorhanden = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((queryParamMap) => queryParamMap.get('palettenVorhanden') ?? 'true')
    ),
    { initialValue: 'true' }
  );

  readonly kommissionierung$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.data
  );

  readonly bewegung = toSignal(
    this.activatedRoute.paramMap.pipe(
      map((paramMap) => paramMap.get('id') ?? ''),
      switchMap((id) =>
        this.kommissionierung$.pipe(
          map((komm) => {
            const index = komm?.bewebskomms.findIndex((o) => o.id + '' === id);

            if (index < 0) return null;

            this.kommissionierMengeLocal =
              komm?.bewebskomms[index].kommissionierDaten.menge;

            return komm?.bewebskomms[index];
          })
        )
      )
    ),
    { initialValue: null }
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
    LlrBestellungStore.getters.loading
  );

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }


  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: OffeneMengeErfassungComponent,
      commands: [
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_WEITER',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-forward-step',
          canExecute: this.gewichtValidOrNotNeeded$.asObservable(),
          execute: async () => this.mengenerfassungAbschliessen() },
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_WEITER',
          class: 'menu bottom-menu primary',
          canExecute: this.gewichtValidOrNotNeeded$.asObservable(),
          execute: async () => this.mengenerfassungAbschliessen() }] });
    this.kommissionierungSubscription = this.kommissionierung$.subscribe((komm) => {
      this.kommissionierung = komm;
    });

    const bewe = this.bewegung();

    if (!bewe) return;

    if (
      bewe?.bewebs?.artikel?.kzgewichtsartikel !== 'J' ||
      (bewe?.bewebs?.artikel?.kzgewichtsartikel === 'J' &&
        bewe.kommissionierDaten.gewicht > 0)
    ) {
      this.gewichtValidOrNotNeeded$.next(true);
    } else {
      this.gewichtValidOrNotNeeded$.next(false);
    }

    this.calculatedAnzahlPaletten = bewe.kommissionierDaten.anz_paletten;
    this.calculatedAnzahlRestmenge = bewe.kommissionierDaten.rest_menge;

    this.vorhandenePalettenAuswaehlbar =
      this.remoteConfig.config.vorhandenePalettenAuswaehlbar;
    this.gewichtEditable = this.remoteConfig.config.gewichtEditable;
    this.paletteEinzelEingabe = this.remoteConfig.config.paletteEinzelEingabe;
  }

  ngOnDestroy(): void {
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      OffeneMengeErfassungComponent
    );

    if (this.kommissionierungSubscription && !this.kommissionierungSubscription.closed)
      this.kommissionierungSubscription.unsubscribe();

    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    if (this.palettenVorhanden() === 'false' || !this.vorhandenePalettenAuswaehlbar) {
      await this.router.navigate([`/llr-bskomm/artikelauswahl`]);
      return;
    } else {
      const palette = this.palettenId();

      if (palette && palette.length > 0) {
        await this.router.navigate([
          `/llr-bskomm/bewegung/${this.bewegung()?.id}/palettenauswahl`]);
        return;
      }
      await this.router.navigate([
        `/llr-bskomm/bewegung/${this.bewegung()?.id}/palettenart-auswahl`]);
    }
  }

  updateMengeLocal(bewegung: KommissionierungBewegung, menge: number) {
    if (bewegung && bewegung.kommissionierDaten) {
      this.kommissionierMengeLocal = menge;
      this.kommissionierGewichtLocal = menge * bewegung.kommissionierDaten.gewichtsfaktor;

      this.calculatedAnzahlPaletten = Math.floor(
        (menge <= 0 ? 1 : menge) /
          ((bewegung?.kommissionierDaten?.menge_pro_palette ?? 0) <= 0
            ? 1
            : bewegung?.kommissionierDaten?.menge_pro_palette)
      );

      this.calculatedAnzahlRestmenge =
        (menge <= 0 ? 1 : menge) %
        ((bewegung?.kommissionierDaten?.menge_pro_palette ?? 0) <= 0
          ? 1
          : bewegung?.kommissionierDaten?.menge_pro_palette);
    }
  }

  updateGewichtLocal(bewegung: KommissionierungBewegung, gewicht: number) {
    if (bewegung && bewegung.kommissionierDaten && gewicht > 0) {
      this.kommissionierGewichtLocal = gewicht;
      this.gewichtValidOrNotNeeded$.next(true);

      return;
    }
    this.gewichtValidOrNotNeeded$.next(false);
  }

  async mengenerfassungAbschliessen() {
    try {
      const bewe = this.bewegung();

      if (!bewe) return;

      bewe.kommissionierDaten.menge =
        this.kommissionierMengeLocal ?? bewe.kommissionierDaten.menge ?? 0;
      bewe.kommissionierDaten.gewicht =
        this.kommissionierGewichtLocal ?? bewe.kommissionierDaten.gewicht ?? 0;

      if (this.kommissionierung?.bewebskomms && bewe) {
        const resultSchreiben = await this.store.dispatch(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
          LlrKommissionierungStore.actions.kommissionierungSchreiben,
          {
            bewe,
            params: {
              idkopfbskomm: this.kommissionierung.id + '',
              idbewebskomm: bewe.id + '',
              idzielpalette: this.palettenId() ? this.palettenId() + '' : '' } }
        );

        if (!resultSchreiben) return;

        const indexBeweOld = this.kommissionierung.bewebskomms.findIndex(
          (o) => o.id === resultSchreiben.id
        );

        if (indexBeweOld >= 0) {
          this.kommissionierung.bewebskomms[indexBeweOld] = resultSchreiben;

          this.store.commit(
            SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
            LlrKommissionierungStore.mutations.patch,
            {
              id: this.kommissionierung.id,
              bewebskomms: this.kommissionierung.bewebskomms }
          );
        }

        const palettenVorhanden =
          this.activatedRoute.snapshot.queryParamMap.get('palettenVorhanden');

        await this.router.navigate(
          [
            `llr-bskomm/bewegung/${bewe.id}/artikeldaten-erfassung/${bewe.id}/mhd-charge-erfassung`],
          {
            queryParams: {
              paletteId: this.palettenId(),
              palettenVorhanden: palettenVorhanden ?? '' } }
        );
      }
      return;
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
